import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import type { UserDetail } from '../api/types';
import { Avatar } from '../components/Avatar';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { KeycloakEventFeed } from '../components/KeycloakEventFeed';
import { PasswordModal } from '../components/PasswordModal';
import { RolePill } from '../components/RolePill';
import { StatusPill } from '../components/StatusPill';
import { UserFormModal } from '../components/UserFormModal';
import { can } from '../lib/roles';
import { getRuntimeConfig } from '../runtimeConfig';
import { useApp } from '../state/AppContext';
import { useToast } from '../state/ToastContext';
import { shortDate } from '../utils/format';
import { photoSrc } from '../utils/photo';
import { tagsOf } from '../utils/tags';

type ConfirmAction = 'suspend' | 'reactivate' | 'activate' | 'delete' | 'reset-password';

interface RevokeTarget {
  systemId: string;
  role: string;
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { me, systems, systemById, refresh } = useApp();
  const canManage = can(me.clientRole, 'manageUsers');
  const canReset = can(me.clientRole, 'resetPassword');
  const canSetPassword = can(me.clientRole, 'setPassword');
  const canDelete = can(me.clientRole, 'deleteUsers');
  const canGrant = can(me.clientRole, 'manageGrants');
  const canViewEvents = can(me.clientRole, 'viewEvents');
  const showActions =
    canManage || canReset || canSetPassword || canDelete || canGrant;
  const { showToast } = useToast();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setUser(await api.user(id));
      setError(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setError('User not found.');
      } else {
        setError(cause instanceof Error ? cause.message : String(cause));
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadEvents = useCallback(
    (first: number, max: number) => api.userKeycloakEvents(id ?? '', { first, max }),
    [id],
  );

  async function revoke() {
    if (!user || !revokeTarget) return;
    const system = systemById(revokeTarget.systemId);
    await api.revoke(user.id, revokeTarget.systemId, revokeTarget.role);
    setRevokeTarget(null);
    showToast(`${system?.name ?? revokeTarget.systemId} access revoked for ${user.name}`);
    await load();
    void refresh();
  }

  async function runConfirm() {
    if (!user || !confirm) return;
    if (confirm === 'suspend') {
      await api.suspendUser(user.id);
      setConfirm(null);
      showToast(`${user.name} suspended`);
      await load();
      void refresh();
    } else if (confirm === 'reactivate') {
      await api.reactivateUser(user.id);
      setConfirm(null);
      showToast(`${user.name} reactivated`);
      await load();
      void refresh();
    } else if (confirm === 'activate') {
      await api.activateUser(user.id);
      setConfirm(null);
      showToast(`${user.name} is now active`);
      await load();
      void refresh();
    } else if (confirm === 'reset-password') {
      const result = await api.resetPassword(user.id);
      setConfirm(null);
      showToast(
        result.emailSent
          ? `Temporary password emailed to ${user.email ?? user.name}`
          : `Temporary password set, but the email failed: ${result.warning ?? 'unknown error'}`,
      );
      await load();
      void refresh();
    } else {
      await api.deleteUser(user.id);
      showToast(`${user.name} deleted`);
      void refresh();
      navigate('/users');
    }
  }

  if (error) {
    return (
      <div className="page">
        <Link className="back-link" to="/users">
          ← All users
        </Link>
        <div className="error-banner">{error}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page">
        <div className="empty-state">Loading user…</div>
      </div>
    );
  }

  const suspended = user.status === 'Suspended';
  const pending = user.status === 'Pending';
  const revokeSystem = revokeTarget ? systemById(revokeTarget.systemId) : null;

  const confirmProps = {
    suspend: {
      title: 'Suspend user',
      message: (
        <>
          Disable the Keycloak account of <strong>{user.name}</strong>. They will be
          unable to sign in until reactivated.
        </>
      ),
      confirmLabel: 'Suspend',
      danger: false,
      requireText: undefined as string | undefined,
    },
    reactivate: {
      title: 'Reactivate user',
      message: (
        <>
          Re-enable the Keycloak account of <strong>{user.name}</strong>.
        </>
      ),
      confirmLabel: 'Reactivate',
      danger: false,
      requireText: undefined as string | undefined,
    },
    activate: {
      title: 'Activate user',
      message: (
        <>
          Clear the pending Keycloak actions of <strong>{user.name}</strong> and mark the
          email address as verified. The account turns Active immediately and the password
          they have now keeps working — they will no longer be asked to change it at the
          next sign-in.
        </>
      ),
      confirmLabel: 'Activate',
      danger: false,
      requireText: undefined as string | undefined,
    },
    'reset-password': {
      title: 'Reset password',
      message: (
        <>
          Set a new temporary password for <strong>{user.name}</strong> and email it to{' '}
          <strong>{user.email ?? 'their address'}</strong>. Their current password stops
          working immediately and they must choose a new one at the next sign-in.
        </>
      ),
      confirmLabel: 'Reset & email',
      danger: false,
      requireText: undefined as string | undefined,
    },
    delete: {
      title: 'Delete user',
      message: (
        <>
          Permanently delete the Keycloak account of <strong>{user.name}</strong>. This
          cannot be undone. Access in other systems is managed separately.
        </>
      ),
      confirmLabel: 'Delete permanently',
      danger: true,
      requireText: user.username,
    },
  };

  return (
    <div className="page page-narrow">
      <Link className="back-link" to="/users">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M10 3L5 8l5 5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        All users
      </Link>

      <div className="detail-header">
        <Avatar
          size="lg"
          name={user.name}
          photoSrc={photoSrc(getRuntimeConfig().apiUrl, user.id, user.photoVersion)}
        />
        <div className="detail-header-meta">
          <div className="detail-title-row">
            <h1>{user.name}</h1>
            <span title="Keycloak account status">
              <StatusPill status={user.status} />
            </span>
            <span className="kc-status-caption">Keycloak account</span>
          </div>
          <div className="detail-subtitle">
            {[user.title, user.department].filter(Boolean).join(' · ') || 'No profile data'}
            {' · '}
            <span className="mono">{user.username}</span>
          </div>
        </div>
        {showActions && (
          <div className="detail-actions">
            {canManage && (
              <button className="btn-secondary" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
            {canReset && (
              <button className="btn-secondary" onClick={() => setConfirm('reset-password')}>
                Reset password
              </button>
            )}
            {canSetPassword && (
              <button className="btn-secondary" onClick={() => setChangingPassword(true)}>
                Change password
              </button>
            )}
            {canManage && pending && (
              <button className="btn-secondary" onClick={() => setConfirm('activate')}>
                Activate
              </button>
            )}
            {canManage && (
              <button
                className="btn-secondary"
                onClick={() => setConfirm(suspended ? 'reactivate' : 'suspend')}
              >
                {suspended ? 'Reactivate' : 'Suspend'}
              </button>
            )}
            {canDelete && (
              <button className="btn-danger-ghost" onClick={() => setConfirm('delete')}>
                Delete
              </button>
            )}
            {canGrant && (
              <Link className="btn-primary" to={`/users/${user.id}/grant`}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3v10M3 8h10"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
                Grant access
              </Link>
            )}
          </div>
        )}
      </div>

      <div className="detail-grid">
        <div className="detail-side">
          <div className="panel">
            <div className="panel-label">Profile</div>
            <div className="field-list">
              <div>
                <div className="field-label">Email</div>
                {user.email ?? '—'}
              </div>
              <div>
                <div className="field-label">Position</div>
                {user.title ?? '—'}
              </div>
              <div>
                <div className="field-label">Department</div>
                {user.department ?? '—'}
              </div>
              <div>
                <div className="field-label">Manager</div>
                {user.manager ?? '—'}
              </div>
              <div>
                <div className="field-label">Location</div>
                {user.location ?? '—'}
              </div>
              <div>
                <div className="field-label">Telephone</div>
                {user.phone ?? '—'}
              </div>
              <div>
                <div className="field-label">Address</div>
                {user.address ?? '—'}
              </div>
              <div>
                <div className="field-label">MFA</div>
                <span className={user.mfaEnabled ? 'mfa-on' : 'mfa-off'}>
                  {user.mfaEnabled ? 'Enabled' : 'Not set'}
                </span>
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-label">Personal contact</div>
            <div className="field-list">
              <div>
                <div className="field-label">Personal email</div>
                {user.personalEmail ?? '—'}
              </div>
              <div>
                <div className="field-label">Personal phone</div>
                {user.personalPhone ?? '—'}
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-label">Employment</div>
            <div className="field-list">
              <div>
                <div className="field-label">Company start</div>
                <span className="mono">{user.companyStart ?? '—'}</span>
              </div>
              <div>
                <div className="field-label">Company leave</div>
                <span className="mono">{user.companyLeave ?? '—'}</span>
              </div>
              <div>
                <div className="field-label">Account created</div>
                <span className="mono">{shortDate(user.joinedAt)}</span>
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-label">Tags</div>
            {tagsOf(user).length > 0 ? (
              <div className="group-row">
                {tagsOf(user).map((tag) => (
                  <span key={tag} className="tag-pill">
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <div className="muted-note">No tags</div>
            )}
          </div>
          <div className="panel">
            <div className="panel-label">Groups</div>
            {user.groups.length > 0 ? (
              <div className="group-row">
                {user.groups.map((group) => (
                  <span key={group} className="group-tag">
                    {group}
                  </span>
                ))}
              </div>
            ) : (
              <div className="muted-note">No groups</div>
            )}
          </div>
        </div>

        <div className="card access-card">
          <div className="access-head">
            <div className="panel-label">System access</div>
            <div className="mono muted">
              {user.grants.length} of {systems.length} systems
            </div>
          </div>
          {user.grants.map((grant) => {
            const system = systemById(grant.systemId);
            return (
              <div key={`${grant.systemId}-${grant.role}`} className="access-row">
                <div className="access-sys">
                  <div className="sys-icon" style={{ background: system?.color ?? '#8a918d' }}>
                    {system?.abbr ?? '?'}
                  </div>
                  <div>
                    <div className="access-sys-name">{system?.name ?? grant.systemId}</div>
                    <div className="access-sys-cat">{system?.category ?? ''}</div>
                  </div>
                </div>
                <div>
                  <RolePill
                    role={grant.role}
                    title={
                      grant.expiresAt ? `Expires ${shortDate(grant.expiresAt)}` : undefined
                    }
                  />
                </div>
                <div className="mono muted">{shortDate(grant.since)}</div>
                {canGrant ? (
                  <div className="access-actions">
                    <button
                      className="btn-revoke"
                      onClick={() =>
                        setRevokeTarget({ systemId: grant.systemId, role: grant.role })
                      }
                    >
                      Revoke
                    </button>
                  </div>
                ) : (
                  <div />
                )}
              </div>
            );
          })}
          {user.grants.length === 0 && (
            <div className="empty-state">
              No system access yet{canGrant ? ' — grant the first permission above.' : '.'}
            </div>
          )}
        </div>
      </div>

      {canViewEvents && (
        <div className="card table-card user-events-card">
          <div className="access-head">
            <div className="panel-label">Keycloak events</div>
            <div className="mono muted">Realm activity for this account</div>
          </div>
          <KeycloakEventFeed
            load={loadEvents}
            pageSize={15}
            emptyLabel="No Keycloak events for this user."
          />
        </div>
      )}

      {editing && (
        <UserFormModal
          mode={{ kind: 'edit', user }}
          onClose={() => setEditing(false)}
          onSaved={(message) => {
            setEditing(false);
            showToast(message);
            void load();
            void refresh();
          }}
        />
      )}

      {changingPassword && (
        <PasswordModal
          user={user}
          onClose={() => setChangingPassword(false)}
          onSaved={(message) => {
            setChangingPassword(false);
            showToast(message);
            void load();
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          {...confirmProps[confirm]}
          onCancel={() => setConfirm(null)}
          onConfirm={runConfirm}
        />
      )}

      {revokeTarget && (
        <ConfirmDialog
          title="Revoke access"
          message={
            <>
              Take <strong>{revokeTarget.role}</strong> access to{' '}
              <strong>{revokeSystem?.name ?? revokeTarget.systemId}</strong> away from{' '}
              <strong>{user.name}</strong>. It stops working immediately; you can grant it
              again at any time.
            </>
          }
          confirmLabel="Revoke access"
          danger
          onCancel={() => setRevokeTarget(null)}
          onConfirm={revoke}
        />
      )}
    </div>
  );
}
